import {FC, useContext, useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {AuthContext} from "../../context/AuthContext";
import {removeSessionCookies} from "../../util/tokenCookies";
import {getJobByKey, getMessagesByKey} from "../../services/api";
import {AxiosError} from "axios";
import {Loader} from "client/src/components/Loader.tsx";
import {IJobDetails} from "../../services/types.ts";

export const MagicLinkRedirect: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const {setError, signIn, setUser} = useContext(AuthContext);
    const hasAttemptedAuth = useRef(false);

    // Capture initial params on mount so they survive re-renders
    const initialParams = useRef(location.search);

    useEffect(() => {
        if (hasAttemptedAuth.current) return;

        const handleMagicLink = async () => {
            const searchParams = new URLSearchParams(initialParams.current);
            const param2 = searchParams.get('param2');
            const id = searchParams.get('id') || "";
            const target = searchParams.get('target');

            const getTargetPath = async (): Promise<string> => {
                switch (target) {
                    case '9mhpkho8n7442ih74cn1wet01':
                    case 'c98fossx47tyng44pr8jd3r4c':
                        return "/finances";
                    case '3qprvnkfc6jfgys0adz20omsb':
                    case '0zyfk7sx422ygdox22tylvwns':
                    case 'candidate_lp_communication_list':
                    case 'candidate_lp_job_detail_communications':
                    case 'client_communication_detail':
                    case 'client_communication_candidate': {
                        const msgId = await getMessagesByKey(id);
                        return `/messages/${msgId[0].id}`;
                    }
                    case 'jobdemand_detail_page':
                    case '90ajomz550bpqje08ki8gix0e':
                    case 'ayfsb5i71ki5216qte71yolhn':
                    case 'd1uqdr81nss8p63cjl2wyeepf': {
                        const messageData: IJobDetails[] = await getMessagesByKey(id);
                        return `/jobs/${messageData[0]?.job_demand_id}`;
                    }
                    case '2ojzpauuxi6nqbk8qw2rmb34g': {
                        const IdofJobFromKey = await getJobByKey(id);
                        return `/jobs/${IdofJobFromKey[0].id}`;
                    }
                    case 'candidate_detail_page':
                    case '1vjzic6pqnq3fgjmt8le5bkmw':
                    case '2ohgbdsg7q1g020lw623gyfg9':
                        return `/details/${id}`;
                    case '26xq2uk7glh17getfoshnntv5':
                        return "/jobs";
                    case 'candidate_lp_profile':
                    case '1kourgddi00entv62rtilafy5':
                        return "/profile";
                    case 'ai_jobs':
                    case 'candidate_ai_jobs':
                        return "/ai-jobs";
                    case 'silverplus':
                    case 'candidate_silverplus':
                        return "/silverplus";
                    case 'courses':
                        return "/courses";
                    case 'cv-generator':
                        return "/cv-generator";
                    default:
                        return "/";
                }
            };

            if (param2 || id) {
                hasAttemptedAuth.current = true;
                try {
                    // Clear old session without navigating (signOut navigates to /login
                    // which unmounts this component and causes race conditions).
                    // We must clear cookies and queries BEFORE signIn so that old
                    // in-flight requests with expired tokens don't trigger 401 interceptor
                    // which would remove our NEW cookie and dispatch a logout event.
                    removeSessionCookies();
                    setUser(undefined as never);
                    await queryClient.cancelQueries();
                    queryClient.clear();

                    await signIn({
                        email: param2 ? "token@app.silvertalent.com" : "token@silvertalent.com",
                        password: param2 || id
                    });

                    const targetPath = await getTargetPath();
                    navigate(targetPath, { replace: true });
                } catch (error: unknown) {
                    if (error instanceof AxiosError) {
                        if (error.response) {
                            setError(error.response.data.message);
                        } else {
                            setError('An unknown error occurred');
                        }
                        console.error('Authentication failed:', error);
                    }
                    navigate('/login');
                }
            } else {
                navigate('/login');
            }
        };

        handleMagicLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <Loader/>;
};
